import React from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import styled from '@emotion/styled'
import { BreakPoint, Styles } from 'src/components/shared/CssValues'
import { Typography } from 'src/components/atoms/Typography'
import { NoLineBreak } from 'src/components/atoms/NoLineBreak'

type FeatureProps = {
  iconURL: string
  label: string
}

const Feature: React.VFC<FeatureProps> = ({ iconURL, label }) => {
  return (
    <FeatureView>
      <img src={iconURL} alt="" />
      <FeatureName>
        {label.split('\n').map((str, index) => (
          <React.Fragment key={index}>
            {str}
            <br />
          </React.Fragment>
        ))}
      </FeatureName>
    </FeatureView>
  )
}

export const PlanCustomerSuccessTeam = () => {
  const data = useStaticQuery(graphql`
    query {
      regularReportImage: file(
        relativePath: { eq: "plan/regular-report.svg" }
      ) {
        publicURL
      }
      workshopImage: file(relativePath: { eq: "plan/workshop.svg" }) {
        publicURL
      }
      todoImage: file(relativePath: { eq: "plan/todo.svg" }) {
        publicURL
      }
      achievementImage: file(relativePath: { eq: "plan/achievement.svg" }) {
        publicURL
      }
      chatSupportImage: file(relativePath: { eq: "plan/chat-support.svg" }) {
        publicURL
      }
      dataScienceTeamImage: file(
        relativePath: { eq: "plan/data-science-team.svg" }
      ) {
        publicURL
      }
    }
  `)

  return (
    <Container>
      <Text>
        <Head>
          <Free>無料</Free>
          <Title>
            <TitleLineBreak>カスタマーサクセスの</TitleLineBreak>
            <TitleLineBreak>充実したサポート</TitleLineBreak>
          </Title>
        </Head>
        <Body>
          目的のすり合わせから立ち上げ準備、実際の運用支援に至るまで、専任のカスタマーサクセスが一貫して伴走。
        </Body>
        <Body>
          コミュニティ領域の最大手として、実践に裏打ちされた戦略知見 /
          戦術の引き出しの多さが、コミュニティ施策の成功確度を最大化します。
        </Body>
      </Text>
      <Features>
        <Feature
          iconURL={data.regularReportImage.publicURL}
          label={`定期レポートと\nディスカッション`}
        />
        <Feature
          iconURL={data.workshopImage.publicURL}
          label={`立ち上げ前の\nワークショップ`}
        />
        <Feature iconURL={data.todoImage.publicURL} label="ToDo管理" />
        <Feature
          iconURL={data.achievementImage.publicURL}
          label={`豊富な\nご支援実績`}
        />
        <Feature
          iconURL={data.chatSupportImage.publicURL}
          label={`Slackでの\n定常的なサポート`}
        />
        <Feature
          iconURL={data.dataScienceTeamImage.publicURL}
          label={`データサイエンス\nチームとの連携`}
        />
      </Features>
    </Container>
  )
}

const Container = styled.div`
  background: ${Styles.color.primary[200]};
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${Styles.spacing[11]} 0;
  padding: ${Styles.spacing[9]} ${Styles.spacing[9]} ${Styles.spacing[11]};
  border-radius: ${Styles.borderRadius[4]};
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    margin: ${Styles.spacing[14]} 0;
    flex-direction: row;
    padding: ${Styles.spacing[13]};
  }
`

const Text = styled.div`
  flex: 1;
  min-width: 0;
  margin: 0 0 ${Styles.spacing[8]} 0;
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    margin: 0 ${Styles.spacing[11]} 0 0;
  }
`

const Head = styled.div`
  display: flex;
  align-items: center;
`

const Free = styled(Typography)`
  border: 3px solid ${Styles.color.secondary[400]};
  color: ${Styles.color.secondary[400]};
  width: 58px;
  height: 58px;
  border-radius: 29px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: ${Styles.letterSpacing[400]};
  font-size: ${Styles.fontSize[400]};
  font-weight: ${Styles.fontWeight.bold};
`

const Title = styled(Typography)`
  flex: 1;
  min-width: 0;
  margin: 0 0 0 ${Styles.spacing[7]};
  line-height: ${Styles.lineHeight[200]};
  letter-spacing: ${Styles.letterSpacing[400]};
  font-weight: ${Styles.fontWeight.bold};
  color: ${Styles.color.primary[400]};
  font-size: ${Styles.fontSize[600]};
`

const TitleLineBreak = styled.span`
  @media screen and (min-width: ${BreakPoint.MOBILE}) {
    display: block;
  }
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    display: inline;
  }
  @media screen and (min-width: 900px) {
    display: block;
  }
`

const Body = styled(Typography)`
  margin: ${Styles.spacing[9]} 0 0 0;
  line-height: ${Styles.lineHeight[400]};
  letter-spacing: ${Styles.letterSpacing[400]};
  font-size: ${Styles.fontSize[300]};
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    font-size: ${Styles.fontSize[400]};
  }
`

const Features = styled.ul`
  width: 100%;
  display: grid;
  row-gap: ${Styles.spacing[9]};
  grid-template-columns: repeat(2, 1fr);
  margin: ${Styles.spacing[8]} 0 0 0;
  @media screen and (min-width: ${BreakPoint.MOBILE}) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    grid-template-columns: repeat(2, 1fr);
    column-gap: ${Styles.spacing[8]};
    margin: 0;
    width: auto;
  }
  @media screen and (min-width: ${BreakPoint.LAPTOP}) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const FeatureView = styled.li`
  text-align: center;
`

const FeatureName = styled(Typography)`
  white-space: nowrap;
  line-height: ${Styles.lineHeight[200]};
  letter-spacing: ${Styles.letterSpacing[400]};
  color: ${Styles.color.primary[400]};
  margin: ${Styles.spacing[3]} 0 0 0;
  font-size: ${Styles.fontSize[200]};
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    font-size: ${Styles.fontSize[300]};
  }
`
